import {
  Block as BlockIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { Alert, IconButton, Tooltip } from '@mui/material';
import React, { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { tradersApi } from 'api';
import {
  CloseDialogResult,
  CloseFormikDialogResult,
  Dialog,
  FormikDialog,
  TimeIntervalSelect,
  TraderActiveOrdersAlert,
} from 'components';
import { QueryKey, UserStatus } from 'enums';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Trader } from 'types';
import { formatUtils } from 'utils';

type Props = {
  trader: Trader;
};

type TrafficValues = {
  unableTakeOrdersDuration: string;
};

export const OperatorTraderActions: React.FC<Props> = ({ trader }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.traders',
  });
  const queryClient = useQueryClient();

  const [isUserStatusDialogOpen, setIsUserStatusDialogOpen] = useState(false);
  const [isTrafficDialogOpen, setIsTrafficDialogOpen] = useState(false);
  const closeUserStatusDialog = useCallback(
    () => setIsUserStatusDialogOpen(false),
    [],
  );
  const closeTrafficDialog = useCallback(
    () => setIsTrafficDialogOpen(false),
    [],
  );

  const isBlocked = trader.user?.status === UserStatus.Disabled;

  const { data: activeOrdersCount } = useQuery(
    [QueryKey.TraderActiveOrdersCount, trader.id],
    () => tradersApi.getActiveOrdersCountAsOperator(trader.id),
    { enabled: isUserStatusDialogOpen && !isBlocked, cacheTime: 0 },
  );

  const userStatusMutation = useMutation(
    tradersApi.updateUserStatusAsOperator,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.Traders);
        closeUserStatusDialog();
      },
    },
  );
  const trafficMutation = useMutation(tradersApi.updateTrafficAsOperator, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.Traders);
      closeTrafficDialog();
    },
  });

  const handleUserStatusDialogClose = useCallback(
    ({ ok }: CloseDialogResult) => {
      if (!ok) {
        return closeUserStatusDialog();
      }
      userStatusMutation.mutate({
        id: trader.id,
        status: isBlocked ? UserStatus.Active : UserStatus.Disabled,
      });
    },
    [closeUserStatusDialog, isBlocked, trader.id, userStatusMutation],
  );

  const handleEnableTrafficDialogClose = useCallback(
    ({ ok }: CloseDialogResult) => {
      if (!ok) {
        return closeTrafficDialog();
      }
      trafficMutation.mutate({
        id: trader.id,
        data: { unableTakeOrders: false },
      });
    },
    [closeTrafficDialog, trader.id, trafficMutation],
  );

  const handleDisableTrafficDialogClose = useCallback(
    ({ ok, data }: CloseFormikDialogResult<TrafficValues>) => {
      if (!ok || !data) {
        return closeTrafficDialog();
      }
      trafficMutation.mutate({
        id: trader.id,
        data: {
          unableTakeOrders: true,
          unableTakeOrdersDuration: Number(
            data.values.unableTakeOrdersDuration,
          ),
        },
      });
    },
    [closeTrafficDialog, trader.id, trafficMutation],
  );

  const userStatusDialogKey = isBlocked ? 'unblock_dialog' : 'block_dialog';

  return (
    <Fragment>
      <div className="tw-flex tw-items-center">
        {isBlocked ? (
          <Tooltip title={t('actions.unblock')}>
            <IconButton
              color="success"
              onClick={() => setIsUserStatusDialogOpen(true)}
            >
              <LockOpenIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t('actions.block')}>
            <IconButton
              color="error"
              onClick={() => setIsUserStatusDialogOpen(true)}
            >
              <BlockIcon />
            </IconButton>
          </Tooltip>
        )}
        {trader.unableTakeOrders ? (
          <Tooltip
            title={`${
              trader.unableTakeOrdersUntilAt
                ? t('unable_take_orders_trader_time', {
                    date: formatUtils.formatDate(
                      trader.unableTakeOrdersUntilAt,
                    ),
                  })
                : t('unable_take_orders_trader')
            }. ${t('actions.enable_traffic')}`}
          >
            <IconButton
              color="warning"
              onClick={() => setIsTrafficDialogOpen(true)}
            >
              <LinkOffIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t('actions.disable_traffic')}>
            <IconButton
              color="primary"
              onClick={() => setIsTrafficDialogOpen(true)}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Dialog
        open={isUserStatusDialogOpen}
        title={t(`actions.${userStatusDialogKey}.title`)}
        mutation={userStatusMutation}
        onClose={handleUserStatusDialogClose}
      >
        <div>{t(`actions.${userStatusDialogKey}.description`)}</div>
        {!isBlocked && <TraderActiveOrdersAlert count={activeOrdersCount} />}
      </Dialog>
      <Dialog
        open={isTrafficDialogOpen && !!trader.unableTakeOrders}
        title={t('actions.enable_traffic_dialog.title')}
        mutation={trafficMutation}
        onClose={handleEnableTrafficDialogClose}
      >
        <div>{t('actions.enable_traffic_dialog.description')}</div>
      </Dialog>
      <FormikDialog
        formikProps={{
          initialValues: { unableTakeOrdersDuration: '0' },
        }}
        dialogProps={{
          open: isTrafficDialogOpen && !trader.unableTakeOrders,
          title: t('actions.disable_traffic_dialog.title'),
          mutation: trafficMutation,
          onClose: handleDisableTrafficDialogClose,
        }}
      >
        {() => (
          <Fragment>
            <div className="tw-mb-4">
              {t('actions.disable_traffic_dialog.description')}
            </div>
            <Alert severity="info" className="tw-mb-4">
              {t('actions.disable_traffic_dialog.active_orders_info')}
            </Alert>
            <TimeIntervalSelect
              name="unableTakeOrdersDuration"
              label={t('actions.disable_traffic_dialog.period')}
              fullWidth
            />
          </Fragment>
        )}
      </FormikDialog>
    </Fragment>
  );
};
